import { useState, useCallback, useRef, useEffect } from 'react';
import { agentService } from '@/services/agent.service';
import type {
  AgentImportSession,
  AgentSessionStatus,
  ProcessedAsset,
  ProcessedTransaction,
} from '@/types/agent.types';
import {
  AgentSessionStatus as AgentSessionStatusEnum,
  isAgentProcessingComplete,
  isAgentProcessingInProgress,
} from '@/types/agent.types';

interface UseAgentProcessingOptions {
  pollIntervalMs?: number;
  onStatusChange?: (session: AgentImportSession) => void;
  onComplete?: (session: AgentImportSession) => void;
  onError?: (error: Error) => void;
}

interface UseAgentProcessingReturn {
  sessionId: string | null;
  session: AgentImportSession | null;
  status: AgentSessionStatus | null;
  isProcessing: boolean;
  isComplete: boolean;
  isFailed: boolean;
  error: string | null;
  processedAssets: ProcessedAsset[];
  processedTransactions: ProcessedTransaction[];
  startPolling: (sessionId: string) => void;
  stopPolling: () => void;
  cancelSession: () => Promise<void>;
  reset: () => void;
}

export function useAgentProcessing(
  options: UseAgentProcessingOptions = {}
): UseAgentProcessingReturn {
  const {
    pollIntervalMs = 2000,
    onStatusChange,
    onComplete,
    onError,
  } = options;

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [session, setSession] = useState<AgentImportSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [processedAssets, setProcessedAssets] = useState<ProcessedAsset[]>([]);
  const [processedTransactions, setProcessedTransactions] = useState<
    ProcessedTransaction[]
  >([]);

  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingRef = useRef(false);
  const sessionIdRef = useRef<string | null>(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    isPollingRef.current = false;
  }, []);

  const fetchProcessedData = useCallback(
    async (sid: string) => {
      try {
        const data = await agentService.getProcessedData(sid);
        if (data.processedAssets) {
          setProcessedAssets(data.processedAssets);
        }
        if (data.processedTransactions) {
          setProcessedTransactions(data.processedTransactions);
        }
      } catch (err) {
        console.error('[useAgentProcessing] Error fetching processed data:', err);
      }
    },
    []
  );

  const pollStatus = useCallback(async () => {
    const currentSessionId = sessionIdRef.current;
    if (!currentSessionId || !isPollingRef.current) {
      return;
    }

    try {
      const currentSession = await agentService.getSessionStatus(currentSessionId);
      setSession(currentSession);
      onStatusChange?.(currentSession);

      if (isAgentProcessingComplete(currentSession.status)) {
        stopPolling();

        if (currentSession.status === AgentSessionStatusEnum.Completed) {
          await fetchProcessedData(currentSessionId);
          onComplete?.(currentSession);
        } else if (
          currentSession.status === AgentSessionStatusEnum.Failed ||
          currentSession.status === AgentSessionStatusEnum.TimedOut ||
          currentSession.status === AgentSessionStatusEnum.Cancelled
        ) {
          setError(
            currentSession.errorMessage ?? 'Agent processing failed'
          );
          onError?.(new Error(currentSession.errorMessage ?? 'Agent processing failed'));
        }
      }
    } catch (err) {
      console.error('[useAgentProcessing] Error polling status:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      onError?.(err instanceof Error ? err : new Error(errorMessage));
      stopPolling();
    }
  }, [onStatusChange, onComplete, onError, stopPolling, fetchProcessedData]);

  const startPolling = useCallback(
    (sid: string) => {
      stopPolling();
      setSessionId(sid);
      sessionIdRef.current = sid;
      setSession(null);
      setError(null);
      setProcessedAssets([]);
      setProcessedTransactions([]);
      isPollingRef.current = true;

      // Initial fetch
      agentService
        .getSessionStatus(sid)
        .then((currentSession) => {
          setSession(currentSession);
          onStatusChange?.(currentSession);

          if (isAgentProcessingComplete(currentSession.status)) {
            isPollingRef.current = false;
            if (currentSession.status === AgentSessionStatusEnum.Completed) {
              fetchProcessedData(sid).then(() => {
                onComplete?.(currentSession);
              });
            } else if (
              currentSession.status === AgentSessionStatusEnum.Failed ||
              currentSession.status === AgentSessionStatusEnum.TimedOut ||
              currentSession.status === AgentSessionStatusEnum.Cancelled
            ) {
              const errorMessage = currentSession.errorMessage ?? 'Agent processing failed';
              setError(errorMessage);
              onError?.(new Error(errorMessage));
            }
          } else {
            // Start polling interval
            pollingRef.current = setInterval(pollStatus, pollIntervalMs);
          }
        })
        .catch((err) => {
          console.error('[useAgentProcessing] Error starting polling:', err);
          const errorMessage = err instanceof Error ? err.message : 'Unknown error';
          setError(errorMessage);
          onError?.(err instanceof Error ? err : new Error(errorMessage));
          isPollingRef.current = false;
        });
    },
    [pollIntervalMs, onStatusChange, onComplete, onError, stopPolling, pollStatus, fetchProcessedData]
  );

  const cancelSession = useCallback(async () => {
    if (!sessionId) {
      return;
    }

    try {
      await agentService.cancelSession(sessionId);
      stopPolling();
      setSession((prev) =>
        prev ? { ...prev, status: AgentSessionStatusEnum.Cancelled } : null
      );
    } catch (err) {
      console.error('[useAgentProcessing] Error cancelling session:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      throw err;
    }
  }, [sessionId, stopPolling]);

  const reset = useCallback(() => {
    stopPolling();
    setSessionId(null);
    sessionIdRef.current = null;
    setSession(null);
    setError(null);
    setProcessedAssets([]);
    setProcessedTransactions([]);
  }, [stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const status = session?.status ?? null;
  const isProcessing = status !== null && isAgentProcessingInProgress(status);
  const isComplete = status === AgentSessionStatusEnum.Completed;
  const isFailed =
    status === AgentSessionStatusEnum.Failed ||
    status === AgentSessionStatusEnum.TimedOut ||
    status === AgentSessionStatusEnum.Cancelled;

  return {
    sessionId,
    session,
    status,
    isProcessing,
    isComplete,
    isFailed,
    error,
    processedAssets,
    processedTransactions,
    startPolling,
    stopPolling,
    cancelSession,
    reset,
  };
}
